import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import GroupTable from '../components/group/GroupTable';
import GroupForm from '../components/group/GroupForm';
import GroupService from '../services/groupService';
import { DealerGroup } from '../types/Group';

const DealerGroupMaintenancePage: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<DealerGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingGroup, setEditingGroup] = useState<DealerGroup | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const data = await GroupService.getAllGroups();
      setGroups(data);
      setError(null);
    } catch (err: any) {
      setError('Error loading dealer groups. Please try again later.');
      console.error('Error fetching dealer groups:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setIsFormOpen(true);
  };

  const handleEditGroup = (group: DealerGroup) => {
    setEditingGroup(group);
    setIsFormOpen(true);
  };

  const handleDeleteGroup = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this dealer group?')) {
      return;
    }

    try {
      await GroupService.deleteGroup(id);
      // Refresh the group list
      fetchGroups();
    } catch (err: any) {
      setError('Error deleting dealer group. Please try again.');
      console.error('Error deleting dealer group:', err);
    }
  };

  const handleFormSubmit = async (group: DealerGroup) => {
    try {
      if (editingGroup) {
        // Update existing group
        await GroupService.updateGroup(editingGroup.id!, group);
      } else {
        // Create new group
        await GroupService.createGroup(group);
      }

      // Close the form and refresh groups
      setIsFormOpen(false);
      setEditingGroup(null);
      fetchGroups();
    } catch (err: any) {
      setError(err.message || 'Error saving dealer group. Please try again.');
      console.error('Error saving dealer group:', err);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingGroup(null);
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToDragDropInterface = () => {
    navigate('/dealer-group-drag-drop');
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={goToDashboard}>
              Dashboard
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Dealer Group Maintenance</span>
          </div>

          <div className="content-container">
            <div className="page-header">
              <h1>Dealer Group Maintenance</h1>
              <div className="button-group">
                <button 
                  className="secondary-button"
                  onClick={goToDragDropInterface}
                >
                  Switch to Drag & Drop
                </button>
                <button 
                  className="primary-button"
                  onClick={handleCreateGroup}
                  disabled={isFormOpen}
                >
                  Add New Group
                </button>
              </div>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {isFormOpen ? (
              <GroupForm 
                group={editingGroup}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
              />
            ) : (
              <GroupTable
                groups={groups}
                loading={loading}
                onEdit={handleEditGroup}
                onDelete={handleDeleteGroup}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealerGroupMaintenancePage; 